import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, LogOut } from 'lucide-react';
import { useStopImpersonation } from '@field-tickets/shared';
import type { ImpersonationSession } from '@field-tickets/shared';
import { showToast } from '@/components/Toast';

function formatRemaining(expiresAt: string, now: number): string {
  const diffMs = new Date(expiresAt).getTime() - now;
  if (diffMs <= 0) return 'Expired';
  const totalSeconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s left`;
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s left`;
}

interface ImpersonationBannerProps {
  session: ImpersonationSession;
}

export function ImpersonationBanner({ session }: ImpersonationBannerProps) {
  const stop = useStopImpersonation();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const remaining = useMemo(() => formatRemaining(session.expiresAt, now), [session.expiresAt, now]);
  const expired = new Date(session.expiresAt).getTime() - now <= 0;

  const displayName = session.targetFullName || 'user';
  const roleLabel = session.targetRole ? session.targetRole.replace('_', ' ') : null;

  async function handleStop() {
    try {
      await stop.mutateAsync();
    } catch (err) {
      // The impersonation cookie may still be active server-side, so do NOT
      // force-reload (that just reloads back into the impersonated identity).
      // Surface the failure and let the admin retry.
      console.error('[Impersonation] stop failed:', err);
      showToast('Failed to stop impersonation', 'error');
      return;
    }
    // Hard reload so every cached query/subscription rebuilds under the admin identity.
    window.location.assign('/admin');
  }

  return (
    <div className="impersonation-banner" role="alert">
      <div className="impersonation-banner__content">
        <AlertTriangle size={18} className="impersonation-banner__icon" aria-hidden="true" />
        <span className="impersonation-banner__text">
          Acting as <strong>{displayName}</strong>
          {roleLabel ? ` (${roleLabel})` : ''}
        </span>
        <span className="impersonation-banner__timer" aria-live="polite">
          {remaining}
        </span>
      </div>
      <button
        type="button"
        className="impersonation-banner__stop"
        onClick={handleStop}
        disabled={stop.isPending}
      >
        <LogOut size={14} aria-hidden="true" />
        {stop.isPending ? 'Stopping…' : expired ? 'Session expired — clear' : 'Stop impersonating'}
      </button>
    </div>
  );
}
